import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import BracketRound from '../BracketRound';
import { CARD_WIDTH, getRoundX } from '../../bracketLayout';
import { PADDING_X, PADDING_Y, STUB_LENGTH, maxHeight } from './geometry';
import { buildConnectors } from './connectors';
import { buildStubs } from './stubs';
import classNames from 'classnames';
import styles from './BracketCanvas.module.scss';

// Todas las rondas siempre montadas. El board se desliza con translateX.
// effectiveIdx se recalcula por estado para comprimir la geometría vertical.
const BracketCanvas = ({ rounds, activeRoundId }) => {
  const scrollRef = useRef(null);
  const activeIdx = Math.max(0, rounds.findIndex(r => r.id === activeRoundId));
  const base = Math.min(activeIdx, 2);

  useEffect(
    () => {
      if (scrollRef.current)
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [activeRoundId],
  );

  const entries = rounds.map((round, i) => ({
    round,
    effectiveIdx: Math.max(0, i - base),
    x: getRoundX(i),
    isPreview: i < activeIdx,
  }));

  const visibleEntries = entries.slice(base);
  const visibleBw = getRoundX(rounds.length - 1 - base) + CARD_WIDTH;
  const bh = maxHeight(visibleEntries);

  // Semi y Final muestran preview de la ronda anterior
  let slideOffset = 0;
  if (activeIdx >= 3) {
    const isMobile = window.innerWidth < 768;
    if (activeIdx === 4) {
      // Final: mostrar preview de semifinal
      const previewPx = isMobile ? 11 : 410;
      slideOffset =
        previewPx - PADDING_X - getRoundX(activeIdx - 1) - CARD_WIDTH;
    } else {
      // Semifinal (activeIdx === 3): posicionar más a la izquierda para mostrar más de Final
      const viewportWidth = window.innerWidth;
      const semiX = getRoundX(activeIdx);
      const centerOffset = (viewportWidth - CARD_WIDTH) / 5;
      slideOffset = centerOffset - semiX - PADDING_X;
    }
  } else if (activeIdx > 0) {
    slideOffset = -getRoundX(activeIdx);
  }

  const showStubs = activeIdx > 0;
  const boardWidth = getRoundX(rounds.length - 1) + CARD_WIDTH;

  return (
    <div ref={scrollRef} className={styles['bracket-canvas']}>
      <div
        className={styles['bracket-canvas__stage']}
        style={{
          width: visibleBw + PADDING_X * 2,
          minHeight: bh + PADDING_Y * 2,
        }}
      >
        <div
          className={styles['bracket-canvas__board']}
          style={{
            position: 'absolute',
            top: PADDING_Y,
            left: PADDING_X,
            width: boardWidth,
            height: bh,
            transform: `translateX(${slideOffset}px)`,
          }}
        >
          {showStubs && (
            <div
              style={{
                position: 'absolute',
                left: getRoundX(base),
                top: 0,
                bottom: 0,
              }}
            >
              {buildStubs(rounds[base], 0, '#9A9AD6')}
            </div>
          )}

          {entries.map(({ round, effectiveIdx, x, isPreview }) => (
            <div
              key={round.id}
              className={classNames(styles['bracket-canvas__round-slot'], {
                [styles['bracket-canvas__round-slot--preview']]: isPreview,
              })}
              style={{ left: x, width: CARD_WIDTH }}
            >
              <BracketRound round={round} roundIndex={effectiveIdx} />
            </div>
          ))}

          <div className={styles['bracket-canvas__connectors']}>
            {buildConnectors(visibleEntries)}
          </div>
        </div>
      </div>
    </div>
  );
};

BracketCanvas.propTypes = {
  rounds: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      matches: PropTypes.array.isRequired,
    }),
  ).isRequired,
  activeRoundId: PropTypes.string,
};

BracketCanvas.defaultProps = { activeRoundId: null };

export default BracketCanvas;
